import React, {useState, useMemo} from 'react';

import {observer} from 'mobx-react';
import {Table, TableBody, TableCell, TableHead, TableRow, Tooltip} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {useStores} from '../../../Hooks';
import ConnectedFlowsModal from './ConnectedFlowsModal';
import {ModelRow} from './ConnectedFlowsRows';

const labelClass = 'block mb-2 text-[11px] font-semibold text-main-normal tracking-[0.5px]';
const tooltipClasses = {tooltip: {className: '!text-base !text-main-normal !bg-background-normal !border !border-panels-normal !shadow-lg !rounded-input'}};
const tableHeaderClass = 'text-xs font-semibold text-main-hover';
const cellSx = {fontSize: 13, color: 'var(--main-normal-color)', borderBottom: '1px solid var(--panels-normal-color)'};

const ConnectedFlowsTable = observer(({threeDModels, isMirrored, connectedFlows}) => {
    const {projectStore, generalStore} = useStores();
    const [editingModelId, setEditingModelId] = useState(null);
    const {connectionsMap, regularProcedures, folders, setModelConnections} = connectedFlows;

    const initialSelected = useMemo(() => {
        if (!editingModelId) return [];
        return Array.from(connectionsMap.get(editingModelId) || new Set());
    }, [editingModelId, connectionsMap]);

    const handleModalSave = (selectedSet) => {
        setModelConnections(editingModelId, selectedSet);
    };

    return (
        <div>
            <div className="flex items-center gap-1">
                <span className={labelClass}>CONNECTED FLOWS</span>
                {isMirrored && (
                    <Tooltip title="Only this workspace's procedures can be managed here" placement="right" enterDelay={200} componentsProps={tooltipClasses}>
                        <InfoOutlinedIcon style={{fontSize: 14, marginBottom: 8}} className="text-main-hover" />
                    </Tooltip>
                )}
            </div>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell className={tableHeaderClass} sx={cellSx}>3D model</TableCell>
                        <TableCell className={tableHeaderClass} sx={cellSx}>{generalStore.translate('connectedFlows')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {threeDModels.map((model) => {
                        const modelId = model.ProcedureIdentifier;
                        const connectedSet = connectionsMap.get(modelId) || new Set();
                        return (
                            <ModelRow
                                key={modelId}
                                model={model}
                                connectedCount={connectedSet.size}
                                totalCount={regularProcedures.length}
                                proceduresLabel={generalStore.translate('procedures', true)}
                                onEdit={() => setEditingModelId(modelId)}
                            />
                        );
                    })}
                </TableBody>
            </Table>
            <ConnectedFlowsModal
                isOpen={!!editingModelId}
                onClose={() => setEditingModelId(null)}
                onSave={handleModalSave}
                initialSelected={initialSelected}
                procedures={regularProcedures}
                folders={folders}
                projectName={projectStore.currentProject?.ProjectName}
            />
        </div>
    );
});

export default ConnectedFlowsTable;
